import { useEffect, useRef, useState, type ReactNode } from 'react';
import { can, formatAge, formatAgeExact, localDate, type ChildDto } from '@bb/shared/core';
import { api } from '../api';
import { Icon, Sheet } from '../components';
import { initials } from '../lib';
import { platform } from '../platform';
import { useApp } from '../state';

/** Аватар малыша: фото или первая буква имени. */
export function ChildAvatar({ child, size = 44 }: { child: ChildDto; size?: number }) {
  return child.photoUrl ? (
    <img className="avatar photo" src={child.photoUrl} alt="" width={size} height={size} style={{ width: size, height: size }} />
  ) : (
    <div className="avatar" aria-hidden="true" style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}>
      {initials(child.name)}
    </div>
  );
}

/**
 * Шапка — как личная страница: фото, имя, точный возраст (мес · нед · дн, в первый год — и число дней).
 * Нажатие на фото — сменить фото; на имя (если детей несколько) — выбрать малыша.
 */
export function ChildHeader({ right, big = false }: { right?: ReactNode; big?: boolean }) {
  const ctx = useApp();
  const { child, family } = ctx;
  const [pick, setPick] = useState(false);
  const [photo, setPhoto] = useState(false);
  const today = localDate(Date.now(), family.tz);
  const many = family.children.length > 1;
  const age = formatAgeExact(child.birthDate, today);
  const canEdit = can(family.role, 'child.edit');

  const nameBlock = (
    <div className="grow" style={{ textAlign: 'left', minWidth: 0 }}>
      <h1 className={`${big ? 'h1' : 'h2'} row`} style={{ gap: 6 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.name}</span>
        {many && <Icon name="down" size={16} />}
      </h1>
      <div className="small muted" data-testid="age">
        {age.main}
        {age.days && <span className="age-days"> ({age.days})</span>}
      </div>
    </div>
  );

  return (
    <header className={`header${big ? ' profile' : ''}`}>
      <button
        type="button"
        className="avatar-btn"
        aria-label={child.photoUrl ? 'Фото малыша' : canEdit ? 'Добавить фото малыша' : child.name}
        onClick={() => (canEdit ? setPhoto(true) : undefined)}
      >
        <ChildAvatar child={child} size={big ? 64 : 44} />
        {canEdit && !child.photoUrl && (
          <span className="avatar-add" aria-hidden="true">
            <Icon name="camera" size={14} />
          </span>
        )}
      </button>
      {many ? (
        <button type="button" className="child-switch grow" aria-haspopup="dialog" aria-label={`Малыш: ${child.name}. Выбрать другого`} onClick={() => setPick(true)}>
          {nameBlock}
        </button>
      ) : (
        nameBlock
      )}
      {right}
      {pick && (
        <Sheet title="Кого смотрим?" onClose={() => setPick(false)}>
          <div className="stack" style={{ gap: 8 }}>
            {family.children.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`pick${c.id === child.id ? ' on' : ''}`}
                aria-pressed={c.id === child.id}
                onClick={() => {
                  ctx.selectChild(c.id);
                  setPick(false);
                }}
              >
                <ChildAvatar child={c} size={40} />
                <span className="grow" style={{ textAlign: 'left' }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>{c.name}</span>
                  <span className="tiny muted">{formatAge(c.birthDate, today)}</span>
                </span>
                {c.id === child.id && <Icon name="check" size={20} />}
              </button>
            ))}
            <button
              type="button"
              className="btn btn-soft"
              onClick={() => {
                setPick(false);
                ctx.openSettings();
              }}
            >
              <Icon name="plus" size={18} /> Добавить малыша
            </button>
          </div>
        </Sheet>
      )}
      {photo && <PhotoSheet onClose={() => setPhoto(false)} />}
    </header>
  );
}

/* ---------- Фото: выбор, кадрирование квадратом, сжатие до 512 px ---------- */

const OUT = 512;
const VIEW = 260;

function PhotoSheet({ onClose }: { onClose: () => void }) {
  const ctx = useApp();
  const { child } = ctx;
  const input = useRef<HTMLInputElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const [busy, setBusy] = useState(false);

  // Базовый масштаб: короткая сторона заполняет квадрат.
  const base = img ? VIEW / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const w = img ? img.naturalWidth * base * zoom : 0;
  const h = img ? img.naturalHeight * base * zoom : 0;
  const clamp = (o: { x: number; y: number }) => ({ x: Math.min(0, Math.max(VIEW - w, o.x)), y: Math.min(0, Math.max(VIEW - h, o.y)) });
  useEffect(() => {
    if (img) setOff(clamp({ x: (VIEW - w) / 2, y: (VIEW - h) / 2 }));
  }, [img]);
  useEffect(() => setOff((o) => clamp(o)), [zoom]);

  function onFile(f: File | undefined) {
    if (!f) return;
    const url = URL.createObjectURL(f);
    const im = new Image();
    im.onload = () => {
      setZoom(1);
      setImg(im);
    };
    im.onerror = () => ctx.toast({ text: 'Не получилось открыть картинку' });
    im.src = url;
  }

  async function save() {
    if (!img) return;
    setBusy(true);
    const c = document.createElement('canvas');
    c.width = OUT;
    c.height = OUT;
    const g = c.getContext('2d')!;
    const k = OUT / VIEW;
    g.drawImage(img, off.x * k, off.y * k, w * k, h * k);
    const dataUrl = c.toDataURL('image/jpeg', 0.85);
    try {
      await api.setPhoto(child.id, dataUrl);
      await ctx.reloadMe();
      platform.haptic('success');
      onClose();
    } catch (e) {
      ctx.fail(e);
      setBusy(false);
    }
  }

  async function remove() {
    if (!(await platform.confirm('Убрать фото?'))) return;
    try {
      await api.setPhoto(child.id, null);
      await ctx.reloadMe();
      onClose();
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <Sheet title="Фото малыша" onClose={onClose}>
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="photo-input" />
      {img ? (
        <>
          <div
            className="crop"
            style={{ width: VIEW, height: VIEW }}
            onPointerDown={(e) => {
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
            }}
            onPointerMove={(e) => {
              const d = drag.current;
              if (d) setOff(clamp({ x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y }));
            }}
            onPointerUp={() => (drag.current = null)}
          >
            <img src={img.src} alt="" draggable={false} style={{ width: w, height: h, transform: `translate(${off.x}px, ${off.y}px)` }} />
          </div>
          <div className="field">
            <span className="label">Масштаб — двигайте фото пальцем</span>
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} aria-label="Масштаб" />
          </div>
          <div className="tiles2">
            <button type="button" className="btn btn-secondary" onClick={() => input.current?.click()}>
              Другое фото
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
              Сохранить
            </button>
          </div>
        </>
      ) : (
        <div className="stack center" style={{ minHeight: 200 }}>
          <ChildAvatar child={child} size={120} />
          <button type="button" className="btn btn-primary" onClick={() => input.current?.click()}>
            <Icon name="camera" size={18} /> {child.photoUrl ? 'Выбрать другое фото' : 'Выбрать фото'}
          </button>
          {child.photoUrl && (
            <button type="button" className="btn btn-danger" onClick={() => void remove()}>
              Убрать фото
            </button>
          )}
          <p className="tiny muted">Фото видят только участники вашей семьи.</p>
        </div>
      )}
    </Sheet>
  );
}
